export const CONCENTRATION_RESOLUTION_STATUSES = Object.freeze([
  'resolved',
  'resolved with conflict',
  'ambiguous',
  'unknown',
])

const VALID_STATUSES = new Set(CONCENTRATION_RESOLUTION_STATUSES)
const VALID_CONFIDENCE = new Set(['verified', 'probable', 'inferred', 'unknown'])

function clean(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function evidenceArray(row) {
  return Array.isArray(row?.concentration_evidence_sources)
    ? row.concentration_evidence_sources
    : []
}

function normalizedEvidenceValue(evidence) {
  return clean(evidence?.normalized_value)
}

function hasOwn(object, key) {
  return object != null && Object.prototype.hasOwnProperty.call(object, key)
}

function hasValidInferredFlag(evidence) {
  return !hasOwn(evidence, 'inferred') || typeof evidence.inferred === 'boolean'
}

function isInferenceEvidence(evidence) {
  // Invalid flag types fail closed at the presentation boundary. SQL rejects
  // them; a stale or malformed client payload still cannot render verified.
  if (!hasValidInferredFlag(evidence)) return true
  return clean(evidence?.source_type).toLowerCase() === 'inference'
    || evidence?.inferred === true
}

function hasVerifiedEvidence(row) {
  const normalized = clean(row?.concentration_normalized)
  if (!normalized) return false
  return evidenceArray(row).some(evidence => (
    hasValidInferredFlag(evidence)
    && !isInferenceEvidence(evidence)
    && clean(evidence?.confidence).toLowerCase() === 'verified'
    && normalizedEvidenceValue(evidence) === normalized
  ))
}

// Catalog resolver fields are the only source of catalog concentration display.
// The frozen legacy `concentration` value remains available for audit/search but
// is deliberately not used as a UI fallback.
export function concentrationPresentation(row = {}) {
  const rawStatus = clean(row.concentration_resolution_status).toLowerCase()
  const status = VALID_STATUSES.has(rawStatus) ? rawStatus : 'unknown'
  const rawDisplay = clean(row.concentration_display)
  const display = status === 'unknown'
    ? 'unknown'
    : status === 'ambiguous'
      ? (rawDisplay && rawDisplay.toLowerCase() !== 'unknown' ? rawDisplay : 'ambiguous')
      : (rawDisplay && rawDisplay.toLowerCase() !== 'unknown' ? rawDisplay : 'unknown')

  const requestedConfidence = clean(row.concentration_resolver_confidence).toLowerCase()
  let confidence = VALID_CONFIDENCE.has(requestedConfidence)
    ? requestedConfidence
    : 'unknown'

  // Defensive UI boundary: a malformed/stale row can never turn inference into
  // a verified badge even before the database constraint is deployed.
  if (confidence === 'verified' && !hasVerifiedEvidence(row)) {
    confidence = evidenceArray(row).some(isInferenceEvidence)
      ? 'inferred'
      : 'unknown'
  }

  const alternates = Array.isArray(row.concentration_alternate_values)
    ? row.concentration_alternate_values.map(clean).filter(Boolean)
    : []
  const conflict = row.concentration_conflict === true
    || status === 'resolved with conflict'

  return {
    display,
    status,
    confidence,
    verified: confidence === 'verified',
    conflict,
    alternates,
    reviewRequired: row.concentration_review_required !== false,
    conflictNote: conflict
      ? `Conflicting sources${alternates.length ? ` — also reported as ${alternates.join(', ')}` : ''}.`
      : '',
  }
}

export function resolvedConcentrationDisplay(row) {
  return concentrationPresentation(row).display
}
